// Identify a routine call at the Monaco cursor and decide whether the OE
// tree carries a matching `stored-procedure` / `function` node.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 9 (routine).
//
// Mirrors the table/view dispatch (Slice 5): we walk the OE tree for nodes
// of `type === 'stored-procedure'` or `'function'` and match against the
// dotted chain at the cursor. The OE tree is the Phase-1 stand-in for the
// SchemaSnapshot; it carries `metadata.objectDetails.id` per node, which is
// the engine's introspection key (PG OID, Oracle/MSSQL object id, MySQL
// schema-qualified routine name) that `fetchObjectDefinitionScript` reads.
//
// Multi-part chains (`schema.proc`, `db.schema.proc`) are accepted — the
// last 1–3 segments name the routine, with earlier segments naming schema
// + database. Unlike table/view dispatch, we do NOT skip 1-part chains —
// procedures rarely come pre-qualified in SQL. We do require the cursor to
// land on the routine identifier itself; arguments inside a `(…)` call live
// past the chain end and never reach this path.
//
// Slice 9 finish (overload preview dialog): `findRoutineCandidatesAtCursor`
// returns ALL OE-tree matches sharing the same qualified name. The
// dispatcher picks the first when there's exactly one and otherwise opens
// the `RoutinePickerDialog` so the user disambiguates between overloads
// (PG / Oracle). MSSQL / MySQL don't support overloading, so the multi-
// candidate list collapses to one element in practice on those engines.

import type { ParseOptions, SqlEngine } from '../../sql-grammar/model';
import { findDottedChainStart, makeQualifiedName, parseDottedChain } from '../../sql-resolver/dotted/dottedParts';
import type { DatabaseExplorerNode } from '../../explorer/models/DatabaseTreeModels';
import { useDatabaseStore } from '../../database-objects/store/databaseStore';
import type { DatabaseEngineId } from '@/core/engines';
import type { IdentifierBinding, QualifiedName } from '../types';
import { databaseEngineIdToSqlEngine } from '../../workspace/visual-query-editor/edit/livePullSchemaSnapshot';
import { foldOeComponent, inferOeComponentQuoting } from '../oeIdentifier';
import { defaultSchemaForEngine } from './externalNavigation';
import { extendCursorToIdentEnd } from './cursorIdent';
import {
  catalogComponent,
  catalogDatabaseForTyped,
  matchesTypedQualification,
  objectComponentEquals,
  oeDatabaseIdentity,
  preferContextDatabase,
  preferExactTypedComponents,
  typedComponent,
} from './oeTreeQualification';

export type RoutineKind = 'stored-procedure' | 'function';

export type RoutineBindingHit = {
  readonly binding: Extract<IdentifierBinding, { kind: 'routine' }>;
  readonly routineKind: RoutineKind;
  /** OE-tree object id — engine-specific introspection key (PG OID, etc.). */
  readonly objectId?: string;
  /** The CATALOG spelling of the database the TYPED chain bound to
   *  (`Sales.dbo.proc` matching a `sales` node → `sales`), `undefined` for 1-
   *  and 2-part chains. Distinct from `binding.target.database`, which is
   *  where the OE tree HAPPENED to find the routine; find-references routing
   *  may only trust this one (see
   *  `find-references/referenceTargetAtCursor.ts`). */
  readonly explicitDatabase?: string;
};

export type FindRoutineDeps = {
  readonly engineId: DatabaseEngineId;
  readonly connectionId: string | null;
  readonly database: string | null;
  /** Allows tests to inject a tree without using the live Zustand store. */
  readonly getTree?: () => readonly DatabaseExplorerNode[];
};

export function findRoutineBindingAtCursor(
  source: string,
  offset: number,
  deps: FindRoutineDeps,
): RoutineBindingHit | null {
  const candidates = findRoutineCandidatesAtCursor(source, offset, deps);
  return candidates[0] ?? null;
}

/**
 * Returns ALL OE-tree routine matches for the qualified name at the cursor.
 * Same OE-tree-as-SchemaSnapshot pattern as the single-hit function, but
 * keeps every match instead of stopping after the first. Used by the
 * dispatcher to drive the `RoutinePickerDialog` when 2+ candidates exist
 * (PG / Oracle overloads).
 *
 * Stored-procedure matches come first, then function matches (same intra-
 * kind precedence as the single-hit function so an explicit pick is stable
 * across re-runs). Result order is also the order the dialog renders rows.
 */
export function findRoutineCandidatesAtCursor(
  source: string,
  offset: number,
  deps: FindRoutineDeps,
): RoutineBindingHit[] {
  if (!deps.connectionId) return [];
  const sqlEngine = databaseEngineIdToSqlEngine(deps.engineId) ?? 'mssql';
  const options: ParseOptions = {
    engine: sqlEngine,
    identifierFolding: defaultFoldingForEngine(sqlEngine),
  };

  const cursorAtEnd = extendCursorToIdentEnd(source, offset);
  const chainStart = findDottedChainStart(source, cursorAtEnd, options);
  const chain = parseDottedChain(source, chainStart, cursorAtEnd, options);
  if (!chain || chain.parts.length === 0) return [];

  const parts = chain.parts.slice(-3);
  const qualified = makeQualifiedName(parts);

  const treeData = (deps.getTree ?? readLiveTree)();
  const procHits = collectFromTree(treeData, deps, qualified, 'stored-procedure', sqlEngine);
  const funcHits = collectFromTree(treeData, deps, qualified, 'function', sqlEngine);
  // Procedures win over functions at the SAME rank, but the tab's own database
  // outranks the kind — the two kinds are walked separately, so the rank each
  // applied internally has to be re-applied across them (the table-vs-view
  // dispatch does the same, see `identifierAtCursor.ts`).
  return [
    ...preferContextDatabase(
      [...procHits, ...funcHits],
      deps.engineId,
      deps.connectionId,
      deps.database,
      (hit) => hit.binding.target.database,
    ),
  ];
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function defaultFoldingForEngine(engine: SqlEngine): ParseOptions['identifierFolding'] {
  if (engine === 'postgres') return 'lower';
  if (engine === 'oracle') return 'upper';
  return 'case-insensitive';
}

type Match = {
  readonly db: string | undefined;
  readonly schema: string | undefined;
  readonly name: string;
  readonly objectId: string | undefined;
};

function collectFromTree(
  treeData: readonly DatabaseExplorerNode[],
  deps: FindRoutineDeps,
  qualified: QualifiedName,
  nodeType: RoutineKind,
  engine: SqlEngine,
): RoutineBindingHit[] {
  // Case-INSENSITIVE on purpose: a cheap superset for the walk, with the
  // precise engine-aware leaf equality applied once below (the table/view
  // dispatch collects the same way — `identifierAtCursor.ts`).
  const targetName = qualified.foldedName.toLowerCase();
  // Both spellings of each typed component — which half an exact comparison
  // reads is per engine (`oeTreeQualification.ts`, FOLDED vs SPELLED).
  const typedDatabase = typedComponent(qualified.foldedDatabase, qualified.database);
  const typedSchema = typedComponent(qualified.foldedSchema, qualified.schema);
  const typedName = typedComponent(qualified.foldedName, qualified.name);
  // The schema the pick PREFERS below: the one the chain spelled out, else the
  // engine's default — a constant, so it is already its own catalog spelling.
  const requestedSchema = typedSchema ?? catalogComponent(defaultSchemaForEngine(deps.engineId));
  const matches: Match[] = [];

  walk(treeData, undefined);
  // Components the chain SPELLED OUT are binding — `OtherDb.dbo.proc` may not
  // resolve to a same-named routine in another database, the same rule the
  // table/view dispatch enforces (see `oeTreeQualification.ts`).
  const gated = matches.filter((m) => {
    if (
      !matchesTypedQualification(
        { database: m.db, schema: m.schema },
        {
          typedDatabase,
          typedSchema,
          contextDatabase: deps.database,
          engineId: deps.engineId,
          connectionId: deps.connectionId,
        },
      )
    ) {
      return false;
    }
    // The routine NAME takes the same engine-aware equality as the schema —
    // SQL Server compares it in the holding DATABASE's collation, pg/oracle
    // byte-exactly after folding. Folding `dbo.P` together with `dbo.p` sent
    // Find References at whichever the tree listed first.
    return objectComponentEquals(
      deps.engineId,
      deps.connectionId,
      oeDatabaseIdentity(deps.engineId, deps.connectionId, m.db, deps.database),
      typedName,
      m.name,
      'routine',
    );
  });
  if (gated.length === 0) return [];
  // The tab's database outranks every other one, so the picker never mixes a
  // routine with same-named "overloads" from a database the user is not in.
  const allowed = preferContextDatabase(
    gated,
    deps.engineId,
    deps.connectionId,
    deps.database,
    (m) => m.db,
  );

  // Schema-qualified callers always specify a schema (`Sales.sp_X`); fall
  // back to the connection's default schema otherwise. We keep ALL same-schema
  // matches instead of collapsing to a single "exact" hit so overloads (same
  // schema, same name, different signatures) all surface in the picker. No
  // database clause: the rank above already decided the database, and a clause
  // pinned to the tab's database emptied this filter whenever the
  // cross-database fallback had to run — every schema's routine then reached
  // the picker.
  // The equality is the SAME engine-aware one the gate uses, read under each
  // candidate's own database identity — a blanket `.toLowerCase()` folded `DBO`
  // into `dbo` after the policy was known, so in a `_CS_` database a bare
  // `sp_x` took whichever of `DBO.sp_x` / `dbo.sp_x` the tree listed first even
  // though only one of them is in the default schema.
  const filtered = allowed.filter((m) =>
    objectComponentEquals(
      deps.engineId,
      deps.connectionId,
      oeDatabaseIdentity(deps.engineId, deps.connectionId, m.db, deps.database),
      requestedSchema,
      m.schema ?? '',
      'schema',
    ),
  );
  // If schema-filtered matches exist, those are the candidates. Otherwise
  // fall back to the name-matched entries the gate allowed (same fallback the
  // original single-hit function had — `chosen = exact ?? matches[0]`).
  const pool = filtered.length > 0 ? filtered : allowed;
  // Among equals, the spelling the user actually typed — the probe-free
  // tie-break for a case-insensitive (or not-yet-probed) policy holding both
  // `dbo.P` and `dbo.p`. Real overloads share one spelling, so it never splits
  // a PG / Oracle overload set.
  const chosen = preferExactTypedComponents(
    pool,
    { schema: qualified.schema, name: qualified.name },
    { schema: (m) => m.schema, name: (m) => m.name },
  );

  return chosen.map((m): RoutineBindingHit => ({
    binding: {
      kind: 'routine',
      target: {
        name: m.name,
        schema: m.schema,
        database: m.db,
        raw: [m.db, m.schema, m.name].filter(Boolean).join('.'),
        // Fold the OE catalog components engine-aware, exactly as the
        // table/view and column dispatches do (oracle unquoted → UPPER, pg →
        // lower, quoted names verbatim). The blanket `.toLowerCase()` this
        // replaces made an oracle routine's identity `get_x` where the walker
        // recorded `GET_X`, so editor-invoked find-references / rename resolved
        // to zero references (the CK-B P1-1 shape, unfixed here until now).
        quoted: inferOeComponentQuoting(engine, m.name),
        foldedName: foldOeComponent(engine, m.name),
        foldedSchema: m.schema ? foldOeComponent(engine, m.schema) : undefined,
        foldedDatabase: m.db ? foldOeComponent(engine, m.db) : undefined,
      },
    },
    routineKind: nodeType,
    objectId: m.objectId,
    explicitDatabase: catalogDatabaseForTyped(qualified.database, m.db),
  }));

  function walk(nodes: readonly DatabaseExplorerNode[] | undefined, currentDb: string | undefined): void {
    if (!nodes) return;
    for (const node of nodes) {
      if (node.type === 'connection') {
        const meta = node.metadata as { id?: string } | undefined;
        if (meta?.id !== deps.connectionId) continue;
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, undefined);
        continue;
      }
      if (node.type === 'database') {
        const meta = node.metadata as { name?: string } | undefined;
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, meta?.name);
        continue;
      }
      if (node.type === nodeType) {
        const meta = node.metadata as
          | { objectDetails?: { id?: string; name?: string; schema?: string } }
          | undefined;
        const od = meta?.objectDetails;
        if (od?.name && od.name.toLowerCase() === targetName) {
          matches.push({ db: currentDb, schema: od.schema, name: od.name, objectId: od.id });
        }
        continue;
      }
      walk(node.children as readonly DatabaseExplorerNode[] | undefined, currentDb);
    }
  }
}

function readLiveTree(): readonly DatabaseExplorerNode[] {
  return useDatabaseStore.getState().treeData;
}
